import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Shield, Users, Gift, Search, LogOut, UserPlus, 
  Building2, Calendar, CheckCircle, XCircle, RefreshCw,
  Eye, Clock, Trash2
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  
  // Data states
  const [users, setUsers] = useState([]);
  const [freeAccessList, setFreeAccessList] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [stats, setStats] = useState({ total: 0, freeAccessCount: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [grantOrgModalOpen, setGrantOrgModalOpen] = useState(false);
  const [grantForm, setGrantForm] = useState({ email: '', freeUntil: '', reason: 'Beta tester' });
  const [grantOrgForm, setGrantOrgForm] = useState({ orgName: '', freeUntil: '', reason: 'Pilot program' });

  // Check if admin key is stored in session
  useEffect(() => {
    const storedKey = sessionStorage.getItem('aura_admin_key');
    if (storedKey) {
      setAdminKey(storedKey);
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Test the admin key by fetching free access list
      const response = await axios.get(`${API}/admin/free-access-list`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      
      sessionStorage.setItem('aura_admin_key', adminKey);
      setIsAuthenticated(true);
      toast.success('Admin access granted');
    } catch (error) {
      toast.error('Invalid admin key');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('aura_admin_key');
    setIsAuthenticated(false);
    setAdminKey('');
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [usersRes, freeRes] = await Promise.all([
        axios.get(`${API}/admin/users?limit=100`, { headers: { 'X-Admin-Key': adminKey } }),
        axios.get(`${API}/admin/free-access-list`, { headers: { 'X-Admin-Key': adminKey } })
      ]);
      
      setUsers(usersRes.data.users || []);
      setStats({ 
        total: usersRes.data.total || 0, 
        freeAccessCount: usersRes.data.free_access_count || 0 
      });
      setFreeAccessList(freeRes.data.users || []);
      
      // Extract unique organizations from users
      const orgs = usersRes.data.users
        .filter(u => u.role === 'org_admin' && u.org_id)
        .map(u => ({ org_id: u.org_id, email: u.email, name: u.name }));
      setOrganizations(orgs);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const grantFreeAccess = async () => {
    if (!grantForm.email) {
      toast.error('Email is required');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        email: grantForm.email,
        reason: grantForm.reason || 'Beta tester',
        free_until: grantForm.freeUntil || null
      };
      
      await axios.post(`${API}/admin/grant-free-access`, payload, {
        headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' }
      });
      
      toast.success(`Free access granted to ${grantForm.email}`);
      setGrantModalOpen(false);
      setGrantForm({ email: '', freeUntil: '', reason: 'Beta tester' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant access');
    } finally {
      setLoading(false);
    }
  };

  const revokeFreeAccess = async (email) => {
    if (!confirm(`Revoke free access for ${email}?`)) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/admin/revoke-free-access`, { email }, {
        headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' }
      });
      
      toast.success(`Free access revoked for ${email}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to revoke access');
    } finally {
      setLoading(false);
    }
  };

  const grantOrgFreeAccess = async () => {
    if (!grantOrgForm.orgName) {
      toast.error('Organization name is required');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        org_name: grantOrgForm.orgName,
        reason: grantOrgForm.reason || 'Pilot program',
        free_until: grantOrgForm.freeUntil || null
      };
      
      await axios.post(`${API}/admin/grant-org-free-access`, payload, {
        headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' }
      });
      
      toast.success(`Free access granted to ${grantOrgForm.orgName}`);
      setGrantOrgModalOpen(false);
      setGrantOrgForm({ orgName: '', freeUntil: '', reason: 'Pilot program' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant org access');
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search
  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-aura-bg flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-aura-surface border-white/10">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-heading text-white uppercase tracking-tight">
              Admin Access
            </CardTitle>
            <p className="text-aura-text-muted text-sm mt-2">
              Enter your admin key to continue
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Admin Key</Label>
                <Input
                  type="password"
                  value={adminKey}
                  onChange={(e) => setAdminKey(e.target.value)}
                  placeholder="Enter admin secret key"
                  className="h-12 bg-aura-bg border-white/10 text-white"
                  data-testid="admin-key-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !adminKey}
                className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-heading uppercase tracking-wider"
                data-testid="admin-login-btn"
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  'Access Dashboard'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-aura-bg">
      {/* Header */}
      <header className="bg-aura-surface border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-white uppercase tracking-tight">Admin Dashboard</h1>
              <p className="text-xs text-aura-text-muted">Aura Back Office</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchAllData}
              disabled={loading}
              className="text-aura-text-muted hover:text-white"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-aura-text-muted hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">{stats.total}</p>
                <p className="text-xs text-aura-text-muted uppercase tracking-wider">Total Users</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Gift className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">{stats.freeAccessCount}</p>
                <p className="text-xs text-aura-text-muted uppercase tracking-wider">Free Access Users</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">{organizations.length}</p>
                <p className="text-xs text-aura-text-muted uppercase tracking-wider">Organizations</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Dialog open={grantModalOpen} onOpenChange={setGrantModalOpen}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700 text-white" data-testid="grant-access-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Grant Free Access
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-aura-surface border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase tracking-tight">Grant Free Access</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">User Email *</Label>
                  <Input
                    type="email"
                    value={grantForm.email}
                    onChange={(e) => setGrantForm({ ...grantForm, email: e.target.value })}
                    placeholder="client@example.com"
                    className="bg-aura-bg border-white/10 text-white"
                    data-testid="grant-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Duration</Label>
                  <Select 
                    value={grantForm.freeUntil} 
                    onValueChange={(v) => setGrantForm({ ...grantForm, freeUntil: v })}
                  >
                    <SelectTrigger className="bg-aura-bg border-white/10 text-white">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent className="bg-aura-surface border-white/10">
                      <SelectItem value="" className="text-white">Permanent (No Expiry)</SelectItem>
                      <SelectItem value={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()} className="text-white">30 Days</SelectItem>
                      <SelectItem value={new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString()} className="text-white">60 Days</SelectItem>
                      <SelectItem value={new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString()} className="text-white">90 Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Reason</Label>
                  <Input
                    value={grantForm.reason}
                    onChange={(e) => setGrantForm({ ...grantForm, reason: e.target.value })}
                    placeholder="Beta tester, Feedback client, etc."
                    className="bg-aura-bg border-white/10 text-white"
                  />
                </div>
                <Button
                  onClick={grantFreeAccess}
                  disabled={loading}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  data-testid="confirm-grant-btn"
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Grant Access'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={grantOrgModalOpen} onOpenChange={setGrantOrgModalOpen}>
            <DialogTrigger asChild>
              <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                <Building2 className="w-4 h-4 mr-2" />
                Grant Org Free Access
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-aura-surface border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase tracking-tight">Grant Organization Free Access</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Organization Name *</Label>
                  <Input
                    value={grantOrgForm.orgName}
                    onChange={(e) => setGrantOrgForm({ ...grantOrgForm, orgName: e.target.value })}
                    placeholder="Lincoln High School"
                    className="bg-aura-bg border-white/10 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Duration</Label>
                  <Select 
                    value={grantOrgForm.freeUntil} 
                    onValueChange={(v) => setGrantOrgForm({ ...grantOrgForm, freeUntil: v })}
                  >
                    <SelectTrigger className="bg-aura-bg border-white/10 text-white">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent className="bg-aura-surface border-white/10">
                      <SelectItem value="" className="text-white">Permanent (No Expiry)</SelectItem>
                      <SelectItem value={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()} className="text-white">30 Days</SelectItem>
                      <SelectItem value={new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString()} className="text-white">90 Days</SelectItem>
                      <SelectItem value={new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString()} className="text-white">6 Months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Reason</Label>
                  <Input
                    value={grantOrgForm.reason}
                    onChange={(e) => setGrantOrgForm({ ...grantOrgForm, reason: e.target.value })}
                    placeholder="Pilot program, Partnership, etc."
                    className="bg-aura-bg border-white/10 text-white"
                  />
                </div>
                <Button
                  onClick={grantOrgFreeAccess}
                  disabled={loading}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Grant Organization Access'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 border-b border-white/10 pb-2">
          <Button
            variant={activeTab === 'users' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('users')}
            className={activeTab === 'users' ? 'bg-aura-secondary' : 'text-aura-text-muted'}
          >
            <Users className="w-4 h-4 mr-2" />
            All Users
          </Button>
          <Button
            variant={activeTab === 'free' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('free')}
            className={activeTab === 'free' ? 'bg-green-600' : 'text-aura-text-muted'}
          >
            <Gift className="w-4 h-4 mr-2" />
            Free Access ({freeAccessList.length})
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-aura-text-muted" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by email or name..."
            className="pl-10 bg-aura-surface border-white/10 text-white"
            data-testid="search-input"
          />
        </div>

        {/* Users Table */}
        {activeTab === 'users' && (
          <Card className="bg-aura-surface border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="users-table">
                <thead className="bg-aura-bg">
                  <tr>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-aura-text-muted font-medium">User</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-aura-text-muted font-medium">Role</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-aura-text-muted font-medium">Status</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-aura-text-muted font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filteredUsers.map((user) => (
                    <tr key={user.user_id} className="hover:bg-white/5 transition-colors">
                      <td className="p-4">
                        <div>
                          <p className="text-white font-medium">{user.name || 'N/A'}</p>
                          <p className="text-sm text-aura-text-muted">{user.email}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge variant="outline" className={`
                          ${user.role === 'athlete' ? 'border-blue-500 text-blue-400' : ''}
                          ${user.role === 'supporter' ? 'border-amber-500 text-amber-400' : ''}
                          ${user.role === 'org_admin' ? 'border-purple-500 text-purple-400' : ''}
                        `}>
                          {user.role}
                        </Badge>
                      </td>
                      <td className="p-4">
                        {user.has_free_access ? (
                          <div className="flex items-center gap-2">
                            <Badge className="bg-green-500/20 text-green-400 border-0">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Free Access
                            </Badge>
                            {user.free_until && (
                              <span className="text-xs text-aura-text-muted">
                                until {new Date(user.free_until).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        ) : (
                          <Badge variant="outline" className="border-white/20 text-aura-text-muted">
                            <Clock className="w-3 h-3 mr-1" />
                            Trial/Standard
                          </Badge>
                        )}
                      </td>
                      <td className="p-4">
                        {user.has_free_access ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => revokeFreeAccess(user.email)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Revoke
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setGrantForm({ ...grantForm, email: user.email });
                              setGrantModalOpen(true);
                            }}
                            className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                          >
                            <Gift className="w-4 h-4 mr-1" />
                            Grant
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={4} className="p-8 text-center text-aura-text-muted">
                        {searchQuery ? 'No users found matching your search' : 'No users yet'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Free Access List */}
        {activeTab === 'free' && (
          <Card className="bg-aura-surface border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="free-access-table">
                <thead className="bg-aura-bg">
                  <tr>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-aura-text-muted font-medium">Email</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-aura-text-muted font-medium">Reason</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-aura-text-muted font-medium">Expires</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-aura-text-muted font-medium">Granted</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-aura-text-muted font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {freeAccessList.map((access, idx) => (
                    <tr key={idx} className="hover:bg-white/5 transition-colors">
                      <td className="p-4">
                        <p className="text-white">{access.email}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-aura-text-muted">{access.reason || 'N/A'}</p>
                      </td>
                      <td className="p-4">
                        {access.free_until ? (
                          <span className="text-amber-400">
                            {new Date(access.free_until).toLocaleDateString()}
                          </span>
                        ) : (
                          <Badge className="bg-green-500/20 text-green-400 border-0">Permanent</Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-aura-text-muted">
                          {access.granted_at ? new Date(access.granted_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </td>
                      <td className="p-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => revokeFreeAccess(access.email)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Revoke
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {freeAccessList.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-aura-text-muted">
                        No users with free access yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
